import React, { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import ToolbarOfferNavItem from '../default/ToolbarOfferNavItem';
import LargeButton from '../../../../common/src/components/LargeButton';
import { matchPath, useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/redux/reducers/rootReducer';
import OffersToolbarPriceSummary from './OffersToolbarPriceSummary';
import { useTranslation } from 'react-i18next';
import RiskObjectIcon from '../../../../common/src/components/RiskObjectIcon';
import { getTextForRiskObject } from '../../services/productFactoryService';
import { getOfferRiskStatus } from '../../services/offersService';
import { Wrap } from '@wegroup/design-system';
import OffersToolbarBackButton from './OffersToolbarBackButton';
import { mqh } from '../../../../common/src/utils/styledComponentsUtils';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import useTracking from '../../../../common/src/hooks/tracking/useTracking';

interface Params {
  sessionId?: string;
  page?: string;
  riskObject?: string;
}

interface SelectedOffers {
  [riskType: string]: string[];
}

const OffersToolbarContainer: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { trackEvent } = useTracking();
  const history = useHistory();
  const { t } = useTranslation();
  // not using useTheme because the need of 'isImmo' prop from the theme
  const theme = useContext(ThemeContext);

  const selectedRiskObjects = useSelector(
    (state: RootState) => state.selection.selectedRiskObjects,
  );
  const visitedOverviewPages = useSelector(
    (state: RootState) => state.offers.visitedOverviewPages,
  );
  const selectedOffers = useSelector(
    (state: RootState) => state.offers.selectedOffers,
  );
  const sessionId = useSessionId();

  const location = useLocation();
  const { page, riskObject }: Params =
    matchPath(location.pathname, {
      path: `/product/session/offers/:page/:riskObject?`,
      exact: true,
    })?.params || {};
  const isSelectedOffersPage = page === 'selectedOffers';
  const handleNav = (navId: string) => {
    if (navId === 'selectedOffers') {
      trackEvent('Continue to basket in Sales Conversation');
      history.push(
        `/product/session/offers/selectedOffers?session_id=${sessionId}`,
      );
    } else
      history.push(
        `/product/session/offers/overview/${navId}?session_id=${sessionId}`,
      );
  };

  const getOfferCount = () => {
    let count = 0;
    Object.values(selectedOffers).forEach((offer) => {
      count = count + offer.length;
    });
    return count;
  };

  return (
    <Wrapper>
      <OffersToolbarBackButton />
      <Wrap
        direction={isSelectedOffersPage ? 'row' : 'column'}
        justifyContent="flex-start"
        spacing="5"
        py="8"
        paddingBottom={isSelectedOffersPage ? '5' : '8'}
        overflow="hidden"
        borderBottom="1px"
        borderColor="secondary.300"
        justify="center"
        flexShrink={0}
      >
        {[...selectedRiskObjects].sort().map((riskKey) => (
          <ToolbarOfferNavItem
            key={`toolbar_item_${riskKey}`}
            Icon={
              <RiskObjectIcon riskObject={riskKey} isImmo={theme['isImmo']} />
            }
            active={riskObject === riskKey}
            onClick={() => handleNav(riskKey)}
            isCollapsed={isSelectedOffersPage}
            status={getOfferRiskStatus(
              riskKey,
              selectedOffers as SelectedOffers,
              visitedOverviewPages as string[],
            )}
          >
            {getTextForRiskObject(riskKey)}
          </ToolbarOfferNavItem>
        ))}
      </Wrap>
      <StyledLargeButton
        active={isSelectedOffersPage}
        onClick={() => handleNav('selectedOffers')}
        dataTestId="ADVISORY-CONVO_OFFERS_TOOLBAR_SELECTED-OFFERS"
      >
        {t('Your selected offers')}
        <SelectedOfferCount
          show={page !== 'selectedOffers' && getOfferCount() > 0}
        >
          {getOfferCount()}
        </SelectedOfferCount>
      </StyledLargeButton>
      <OffersToolbarPriceSummary show={isSelectedOffersPage} />
    </Wrapper>
  );
};

const SelectedOfferCount = styled.div<{ show?: boolean }>`
  width: 2.3rem;
  height: 2.3rem;
  display: ${({ show }) => (show ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background-color: white;
  color: ${({ theme }) => theme.primary[500]};
  font-size: 1.2rem;
  line-height: 100%;
  border-radius: 50px;
  font-weight: 700;
  margin-left: 1.6rem;
  transition: 0.2s;
  flex-shrink: 0;
`;

const StyledLargeButton = styled(LargeButton)<{ active?: boolean }>`
  margin-top: 0.8rem;
  background-color: ${({ theme, active }) => active && theme.gray[50]};
  color: ${({ theme, active }) => active && theme.gray[300]};
  display: flex;
  margin-right: 1.6rem;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    background-color: ${({ theme, active }) => active && theme.gray[100]};
  }

  ${mqh.fhd} {
    margin-top: 2.4rem;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: -4rem;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;
`;

export default OffersToolbarContainer;
